import { type Configuration, LogLevel } from "@azure/msal-browser";
import { logger } from '@/lib/logger';

/**
 * MSAL Configuration for Microsoft Entra ID (Azure AD) Authentication
 * 
 * This configuration uses the Authorization Code Flow with PKCE (Proof Key for Code Exchange)
 * which is the recommended flow for Single Page Applications.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || "",
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || "common"}`,
    redirectUri: `${window.location.origin}/app`, // Always redirect to /app after login
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // sessionStorage: tokens are cleared when the browser closes and are NOT
    // accessible from other tabs. This reduces the XSS attack surface compared
    // to localStorage (which persists tokens indefinitely and across tabs).
    cacheLocation: "sessionStorage",
  },
  system: {
    // Prevents the hidden iframe (used for silent token renewal) from trying to
    // navigate the top-level window, which causes "sandboxed frame" console errors.
    allowRedirectInIframe: true,
    loggerOptions: {
      logLevel: LogLevel.Warning, // Changed back to Warning (production ready)
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        
        switch (level) {
          case LogLevel.Error:
            logger.error('MSAL', message);
            break;
          case LogLevel.Warning:
            logger.warn('MSAL', message);
            break;
          case LogLevel.Info:
            logger.info('MSAL', message);
            break;
          case LogLevel.Verbose:
            logger.debug('MSAL', message);
            break;
        }
      },
    },
  },
};

/**
 * Scopes for login requests
 * These scopes are requested when the user logs in
 */
export const loginRequest = {
  scopes: ["User.Read", "openid", "profile", "email"],
  // Force authorization code flow with PKCE
  responseMode: "query" as const, // Use query parameters instead of hash fragments
};

/**
 * Scopes for API calls to your backend
 * Adjust the scope to match your backend API configuration
 */
export const apiRequest = {
  scopes: [`api://${import.meta.env.VITE_AZURE_CLIENT_ID}/access_as_user`],
};

/**
 * Scopes for Microsoft Graph API calls
 */
export const graphRequest = {
  scopes: ["User.Read"],
};
